// Padding (as a fraction of the in-range span) added on each side of the
// reference range so the in-range zone never spans the full bar — gives the
// "lighter outer / darker inner" two-zone look from the Figma spec.
const VIEWPORT_PADDING_RATIO = 0.25;

export interface RangeBarViewport {
  /** Left edge of the darker in-range zone, as a percent of the full track */
  inRangeStartPct: number;
  /** Right edge of the darker in-range zone, as a percent of the full track */
  inRangeEndPct: number;
  /** Indicator dot position as a percent of the full track, or null if no numeric value */
  valuePct: number | null;
}

/**
 * Computes the in-range zone bounds and the indicator dot position for the
 * lab parameter range bar. Returns null when low/high are missing or invalid
 * (no bar should be drawn in that case).
 *
 * The viewport is widened by 25% of the range on each side so the in-range
 * zone always sits inset from the track edges. If the actual value falls
 * outside the padded viewport, the viewport stretches further to keep the
 * indicator visible.
 */
export function computeRangeBarViewport(
  rangeLow: number | null | undefined,
  rangeHigh: number | null | undefined,
  value: number | null | undefined
): RangeBarViewport | null {
  if (
    rangeLow === null ||
    rangeLow === undefined ||
    rangeHigh === null ||
    rangeHigh === undefined ||
    rangeHigh <= rangeLow
  ) {
    return null;
  }

  const span = rangeHigh - rangeLow;
  let viewportMin = rangeLow - VIEWPORT_PADDING_RATIO * span;
  let viewportMax = rangeHigh + VIEWPORT_PADDING_RATIO * span;

  if (value !== null && value !== undefined && Number.isFinite(value)) {
    if (value < viewportMin) viewportMin = value - 0.1 * span;
    if (value > viewportMax) viewportMax = value + 0.1 * span;
  }

  const total = viewportMax - viewportMin;
  const inRangeStartPct = ((rangeLow - viewportMin) / total) * 100;
  const inRangeEndPct = ((rangeHigh - viewportMin) / total) * 100;
  const valuePct =
    value !== null && value !== undefined && Number.isFinite(value)
      ? ((value - viewportMin) / total) * 100
      : null;

  return {
    inRangeStartPct,
    inRangeEndPct,
    valuePct,
  };
}
